import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import { TermsContent } from './TermsContent';

const SCROLL_THRESHOLD = 50; // pixels from bottom to consider "scrolled"

interface TermsAgreementDialogProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
}

export const TermsAgreementDialog = ({ open, onClose, onAccept }: TermsAgreementDialogProps) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    if (distanceFromBottom <= SCROLL_THRESHOLD && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  }, [hasScrolledToBottom]);

  const handleClose = () => {
    setHasScrolledToBottom(false);
    onClose();
  };

  const handleAccept = () => {
    setHasScrolledToBottom(false);
    onAccept();
    onClose();
  };

  // If content fits without scrolling, enable the button immediately
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      const el = scrollRef.current;
      if (el && el.scrollHeight <= el.clientHeight + SCROLL_THRESHOLD) {
        setHasScrolledToBottom(true);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle>Terms of Service</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2, color: 'text.secondary' }}>
          Effective Date: 15 February 2025
        </Typography>
        <Box
          ref={scrollRef}
          onScroll={handleScroll}
          sx={{
            maxHeight: 400,
            overflowY: 'auto',
            pr: 1,
          }}
        >
          <TermsContent />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Please scroll to the bottom to confirm you have read the Terms of Service.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleAccept} disabled={!hasScrolledToBottom}>
            I have read and agree to the Terms of Service
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
